import { Paper, TableCell, TableRow, Typography } from '@mui/material';
import React from 'react';
import { allValuesEqual, formatDollars, formatPercentage, uuidv4 } from '../../../../../common';
import { ClientSideDataTable } from '../../../../../common/components/dataTable/clientSide';
import { DatatableColumn } from '../../../../../common/components/dataTable/types';
import { LoadingProgress } from '../../../../../common/store/types';
import { PortfolioSecurityPerformanceTableGroupItem, PortfolioSecurityPerformanceTableResult } from '../store/types';

export interface SecurityPerformanceTableProps {
  portfolioSecurityPerformanceProgress: LoadingProgress;
  portfolioSecurityPerformanceResults?: PortfolioSecurityPerformanceTableResult;
  rowsExpanded?: number[];
}

export const SecurityPerformanceTable = (props: SecurityPerformanceTableProps): JSX.Element => {
  const { portfolioSecurityPerformanceResults, portfolioSecurityPerformanceProgress } = props;
  const results = portfolioSecurityPerformanceResults?.groupItems ?? [];

  const assetColumn = (dataIndex: number): React.ReactNode => {
    return (
      <Typography variant="h5" color={'primary'}>
        {results[dataIndex]?.groupName ?? ''}
      </Typography>
    );
  };

  const openColumn = (dataIndex: number): React.ReactNode => {
    return <Typography variant="h5">{formatDollars(results[dataIndex]?.openingBalance ?? 0)}</Typography>;
  };

  const aquisitionsColumn = (dataIndex: number): React.ReactNode => {
    return <Typography variant="h5">{formatDollars(results[dataIndex]?.aquisitions ?? 0)}</Typography>;
  };

  const disposalsColumn = (dataIndex: number): React.ReactNode => {
    return <Typography variant="h5">{formatDollars(results[dataIndex]?.disposals ?? 0)}</Typography>;
  };

  const capitalMovementColumn = (dataIndex: number): React.ReactNode => {
    return <Typography variant="h5">{formatDollars(results[dataIndex]?.capitalMovement ?? 0)}</Typography>;
  };

  const otherColumn = (dataIndex: number): React.ReactNode => {
    return <Typography variant="h5">{formatDollars(results[dataIndex]?.other ?? 0)}</Typography>;
  };

  const closingBalanceColumn = (dataIndex: number): React.ReactNode => {
    return <Typography variant="h5">{formatDollars(results[dataIndex]?.closingBalance ?? 0)}</Typography>;
  };

  const growthColumn = (dataIndex: number): React.ReactNode => {
    const growth: number = results[dataIndex]?.growth ?? 0;
    return (
      <>
        <Typography variant="h5" align="right" color={!growth ? 'textPrimary' : growth < 0 ? 'error' : 'secondary'}>
          {formatDollars(growth)}
        </Typography>
        <Typography variant="h6" align="right" color={'textSecondary'}>
          {formatPercentage(results[dataIndex]?.growthReturn ?? 0)}
        </Typography>
      </>
    );
  };

  const incomeColumn = (dataIndex: number): React.ReactNode => {
    const income: number = results[dataIndex]?.income ?? 0;
    return (
      <>
        <Typography variant="h5" align="right" color={!income ? 'textPrimary' : income < 0 ? 'error' : 'secondary'}>
          {formatDollars(income)}
        </Typography>
        <Typography variant="h6" align="right" color={'textSecondary'}>
          {formatPercentage(results[dataIndex]?.incomeReturn ?? 0)}
        </Typography>
      </>
    );
  };

  const totalColumn = (dataIndex: number): React.ReactNode => {
    const total: number = results[dataIndex]?.total ?? 0;
    return (
      <>
        <Typography variant="h5" align="right" color={!total ? 'textPrimary' : total < 0 ? 'error' : 'secondary'}>
          {formatDollars(total)}
        </Typography>
        <Typography variant="h6" align="right" color={'textSecondary'}>
          {formatPercentage(results[dataIndex]?.totalReturn ?? 0)}
        </Typography>
      </>
    );
  };

  const detailedColumn = (
    rowData: string[],
    rowMeta: {
      dataIndex: number;
      rowIndex: number;
    }
  ): React.ReactNode => {
    const rootGroupItem = results[rowMeta.dataIndex];
    if (!rootGroupItem || allValuesEqual<PortfolioSecurityPerformanceTableGroupItem, number>('number', 0, rootGroupItem)) {
      return <></>;
    }
    return (
      <>
        {(rootGroupItem?.children ?? [])
          .filter((item) => !allValuesEqual<PortfolioSecurityPerformanceTableGroupItem, number>('number', 0, item))
          .map((item) => (
            <TableRow key={uuidv4()}>
              <TableCell style={{ padding: '0' }} />
              <TableCell>
                <>
                  <Typography variant="h5" color={'primary'} noWrap style={{ minWidth: '119px' }}>
                    {(item?.securityCode ?? '').split('.')[0]}
                  </Typography>
                  <Typography variant="h6" color={'textSecondary'} noWrap style={{ minWidth: '119px' }}>
                    {item?.securityName ?? ''}
                  </Typography>
                </>
              </TableCell>
              <TableCell>
                <Typography variant="h5" align={'right'}>
                  {formatDollars(item?.openingBalance ?? 0)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="h5" align={'right'}>
                  {formatDollars(item?.aquisitions ?? 0)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="h5" align={'right'}>
                  {formatDollars(item?.disposals ?? 0)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="h5" align={'right'}>
                  {formatDollars(item?.capitalMovement ?? 0)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="h5" align={'right'}>
                  {formatDollars(item?.other ?? 0)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="h5" align={'right'}>
                  {formatDollars(item?.closingBalance ?? 0)}
                </Typography>
              </TableCell>
              <TableCell>
                <>
                  <Typography variant="h5" align="right" color={!item?.growth ? 'textPrimary' : (item?.growth ?? 0) < 0 ? 'error' : 'secondary'}>
                    {formatDollars(item?.growth ?? 0)}
                  </Typography>
                  <Typography variant="h6" align="right" color={'textSecondary'}>
                    {formatPercentage(item?.growthReturn ?? 0)}
                  </Typography>
                </>
              </TableCell>
              <TableCell>
                <>
                  <Typography variant="h5" align="right" color={!item?.income ? 'textPrimary' : (item?.income ?? 0) < 0 ? 'error' : 'secondary'}>
                    {formatDollars(item?.income ?? 0)}
                  </Typography>
                  <Typography variant="h6" align="right" color={'textSecondary'}>
                    {formatPercentage(item?.incomeReturn ?? 0)}
                  </Typography>
                </>
              </TableCell>
              <TableCell>
                <>
                  <Typography variant="h5" align="right" color={!item?.total ? 'textPrimary' : (item?.total ?? 0) < 0 ? 'error' : 'secondary'}>
                    {formatDollars(item?.total ?? 0)}
                  </Typography>
                  <Typography variant="h6" align="right" color={'textSecondary'}>
                    {formatPercentage(item?.totalReturn ?? 0)}
                  </Typography>
                </>
              </TableCell>
            </TableRow>
          ))}
      </>
    );
  };

  const columns: DatatableColumn[] = [
    {
      name: 'ASSETCLASSSECURITY',
      label: 'ASSET CLASS & SECURITY',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => assetColumn(dataIndex),
      },
    },
    {
      name: 'OPENINGVALUE',
      label: 'OPENING VALUE',
      textAlign: 'right',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => openColumn(dataIndex),
        setCellHeaderProps: () => {
          return {
            style: { textAlign: 'right' },
          };
        },
      },
    },
    {
      name: 'PURCHASES',
      label: 'PURCHASES',
      textAlign: 'right',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => aquisitionsColumn(dataIndex),
        setCellHeaderProps: () => {
          return {
            style: { textAlign: 'right' },
          };
        },
      },
    },
    {
      name: 'SALES',
      label: 'SALES',
      textAlign: 'right',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => disposalsColumn(dataIndex),
        setCellHeaderProps: () => {
          return {
            style: { textAlign: 'right' },
          };
        },
      },
    },
    {
      name: 'TRANSFERSCORPACTIONS',
      label: 'TRANSFERS & CORP ACTIONS',
      textAlign: 'right',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => capitalMovementColumn(dataIndex),
        setCellHeaderProps: () => {
          return {
            style: { textAlign: 'right' },
          };
        },
      },
    },
    {
      name: 'OTHER',
      label: 'OTHER',
      textAlign: 'right',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => otherColumn(dataIndex),
        setCellHeaderProps: () => {
          return {
            style: { textAlign: 'right' },
          };
        },
      },
    },
    {
      name: 'CLOSINGVALUE',
      label: 'CLOSING VALUE',
      textAlign: 'right',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => closingBalanceColumn(dataIndex),
        setCellHeaderProps: () => {
          return {
            style: { textAlign: 'right' },
          };
        },
      },
    },
    {
      name: 'CAPITALGAIN/LOSSES',
      label: 'CAPITAL GAIN/LOSSES',
      textAlign: 'right',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => growthColumn(dataIndex),
        setCellHeaderProps: () => {
          return {
            style: { textAlign: 'right' },
          };
        },
      },
    },
    {
      name: 'INCOME',
      label: 'INCOME',
      textAlign: 'right',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => incomeColumn(dataIndex),
        setCellHeaderProps: () => {
          return {
            style: { textAlign: 'right' },
          };
        },
      },
    },
    {
      name: 'TOTAL RETURN',
      label: 'TOTAL RETURN',
      textAlign: 'right',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => totalColumn(dataIndex),
        setCellHeaderProps: () => {
          return {
            style: { textAlign: 'right' },
          };
        },
      },
    },
  ];

  return (
    <Paper elevation={0} style={{ marginBottom: '40px' }}>
      <ClientSideDataTable
        loadingProgress={portfolioSecurityPerformanceProgress}
        columns={columns}
        data={results}
        options={{
          filter: false,
          expandableRows: true,
          expandableRowsOnClick: true,
          rowsExpanded: props.rowsExpanded,
          renderExpandableRow: (rowData, rowMeta) => detailedColumn(rowData, rowMeta),
        }}
        id="client-performance-datatable"
      ></ClientSideDataTable>
    </Paper>
  );
};
